import { Club, Competition, CompetitionFixture, CompetitionPerformance, Player } from '../types'

export const CUSTOM_COMPETITION_VALUE = '__custom__'

export const COMPETITION_POLICY_NOTICE =
  'Only recognized competitions, tournaments, and cups may be recorded. Friendly, training, exhibition, and unofficial fixtures are not allowed.'

const BANNED_COMPETITION_TERMS = [
  'friendly',
  'training',
  'unofficial',
  'exhibition',
  'scrimmage',
  'practice match',
  'practice game',
  'trial match',
]

export const APPROVED_COMPETITION_EXAMPLES = [
  'NPFL',
  'NNL',
  'NLO',
  'NWFL',
  'State FA Cup',
  'TCC',
  'N-Youth League',
  'NextGen Football Competition',
  'Gothia Cup',
  'Federation Cup',
]

export const normalizeCompetitionInput = (competition?: string | null) =>
  competition?.trim().toLowerCase().replace(/\s+/g, ' ') || ''

export const getCompetitionPolicyViolation = (competition?: string | null) => {
  const normalized = normalizeCompetitionInput(competition)
  if (!normalized) return 'A competition or tournament name is required.'

  const blockedTerm = BANNED_COMPETITION_TERMS.find((term) => normalized.includes(term))
  if (blockedTerm) {
    return `Competition data from "${blockedTerm}" fixtures is not allowed.`
  }

  return ''
}

export const isEligibleCompetition = (competition?: string | null) => !getCompetitionPolicyViolation(competition)

export const collectCompetitionNames = ({
  competitions = [],
  clubs = [],
  fixtures = [],
  player = null,
}: {
  competitions?: Competition[]
  clubs?: Club[]
  fixtures?: CompetitionFixture[]
  player?: Player | null
}) => {
  const names = new Set<string>()

  competitions.forEach((competition) => {
    if (competition?.name?.trim()) names.add(competition.name.trim())
  })

  clubs.forEach((club) => {
    club.competitions?.forEach((competition) => {
      if (competition?.trim()) names.add(competition.trim())
    })
  })

  fixtures.forEach((fixture) => {
    if (fixture?.competition_name?.trim()) names.add(fixture.competition_name.trim())
    if ((fixture as any)?.competition?.trim()) names.add((fixture as any).competition.trim())
  })

  if (player) {
    const matchHistory = player.match_history || []
    matchHistory.forEach((match) => {
      if (match?.competition?.trim()) names.add(match.competition.trim())
    })
    player.competitions?.forEach((competition) => {
      if (competition?.trim()) names.add(competition.trim())
    })
    const performanceHistory = player.competition_performances || []
    performanceHistory.forEach((performance: CompetitionPerformance) => {
      if (performance?.competition_name?.trim()) names.add(performance.competition_name.trim())
    })
    if (player.league?.trim()) names.add(player.league.trim())
  }

  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

export const getCompetitionLabel = (competition?: string | null, fallback = 'Unknown Competition') =>
  competition && competition.trim() ? competition.trim() : fallback

export const matchCompetitionName = (competitionId: string | undefined, competitions: Competition[]) => {
  if (!competitionId) return ''
  return competitions.find((competition) => competition.id === competitionId)?.name || ''
}

export const inferCompetitionId = (competitionName: string, competitions: Competition[]) => {
  const normalized = competitionName.trim().toLowerCase()
  return competitions.find((competition) => competition.name.trim().toLowerCase() === normalized)?.id
}
